import TableContainer from "Common/TableContainer";
import { CallAPIGetPromise } from "Common/commaon";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import moment from "moment";
import { getDecodeId, getUserType } from "slices/userProfile/thunk";
import {
  LOGAN_SUPPORT_TICKET_CREATED_EVENT,
  openLoganSupportTicket,
} from "features/productAssistant/events";

interface SupportTicket {
  sp_id: number;
  sp_title?: string;
  sp_description?: string;
  sp_created_by?: number | string;
  sp_status?: string;
  sp_created_at?: string;
}

const normalizeStatus = (status?: string) => (status || "OPEN").trim().toUpperCase();

const statusBadge = (status?: string) => {
  const normalizedStatus = normalizeStatus(status);

  if (["CLOSED", "CLOSE", "RESOLVED"].includes(normalizedStatus)) {
    return <Badge bg="success-subtle" text="success">Closed</Badge>;
  }
  if (normalizedStatus === "PENDING") {
    return <Badge bg="warning-subtle" text="warning">Pending</Badge>;
  }
  if (normalizedStatus === "NEW") {
    return <Badge bg="info-subtle" text="info">New</Badge>;
  }

  return <Badge bg="primary-subtle" text="primary">Open</Badge>;
};

const ListViewTickets = () => {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const userType = getUserType();
  const isAdmin = userType === "admin";

  const loadTickets = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const endpoint = isAdmin
        ? "/v1/api/support/get"
        : `/v1/api/support/get/${getDecodeId()}`;
      const response: any = await CallAPIGetPromise(endpoint);
      const rows = response?.data?.subscribers;
      setTickets(Array.isArray(rows) ? rows : []);
    } catch (_error) {
      setTickets([]);
      setError("We couldn't load your support tickets. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    document.title = "Support Tickets | Tradesmens";
    void loadTickets();

    const refreshTickets = () => void loadTickets();
    window.addEventListener(LOGAN_SUPPORT_TICKET_CREATED_EVENT, refreshTickets);
    return () => window.removeEventListener(LOGAN_SUPPORT_TICKET_CREATED_EVENT, refreshTickets);
  }, [loadTickets]);

  const filteredTickets = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return tickets;

    return tickets.filter((ticket) =>
      [
        ticket.sp_id,
        ticket.sp_title,
        ticket.sp_description,
        ticket.sp_status,
        ticket.sp_created_at && moment(ticket.sp_created_at).format("DD MMM YYYY"),
      ].some((value) => String(value || "").toLowerCase().includes(query))
    );
  }, [search, tickets]);

  const counts = useMemo(() => {
    const statuses = tickets.map((ticket) => normalizeStatus(ticket.sp_status));
    return {
      total: tickets.length,
      open: statuses.filter((status) => ["OPEN", "NEW", ""].includes(status)).length,
      pending: statuses.filter((status) => status === "PENDING").length,
      closed: statuses.filter((status) => ["CLOSED", "CLOSE", "RESOLVED"].includes(status)).length,
    };
  }, [tickets]);

  const columns = useMemo(() => {
    const ticketColumns: any[] = [
      {
        Header: "Ticket ID",
        accessor: "sp_id",
        Filter: false,
        isSortable: true,
        Cell: ({ row }: any) => (
          <span className="fw-semibold text-primary">#{row.original.sp_id}</span>
        ),
      },
      {
        Header: "Subject",
        accessor: "sp_title",
        Filter: false,
        isSortable: true,
        Cell: ({ row }: any) => (
          <div style={{ minWidth: 180 }}>
            <div className="fw-semibold text-body">{row.original.sp_title || "Untitled ticket"}</div>
            {row.original.sp_description && (
              <div className="text-muted fs-sm text-truncate" style={{ maxWidth: 420 }} title={row.original.sp_description}>
                {row.original.sp_description}
              </div>
            )}
          </div>
        ),
      },
    ];

    if (isAdmin) {
      ticketColumns.push({
        Header: "Created By",
        accessor: "sp_created_by",
        Filter: false,
        isSortable: true,
      });
    }

    ticketColumns.push(
      {
        Header: "Created",
        accessor: "sp_created_at",
        Filter: false,
        isSortable: true,
        Cell: ({ row }: any) => (
          <span className="text-muted">
            {row.original.sp_created_at
              ? moment(row.original.sp_created_at).format("DD MMM YYYY, h:mm A")
              : "—"}
          </span>
        ),
      },
      {
        Header: "Status",
        accessor: "sp_status",
        Filter: false,
        isSortable: true,
        Cell: ({ row }: any) => statusBadge(row.original.sp_status),
      }
    );

    return ticketColumns;
  }, [isAdmin]);

  const summaryCards = [
    { label: "Total Tickets", value: counts.total, icon: "ph-ticket", color: "primary" },
    { label: "Open", value: counts.open, icon: "ph-envelope-open", color: "info" },
    { label: "Pending", value: counts.pending, icon: "ph-clock", color: "warning" },
    { label: "Closed", value: counts.closed, icon: "ph-check-circle", color: "success" },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="g-3 mb-4">
          {summaryCards.map((item) => (
            <Col xl={3} sm={6} key={item.label}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="d-flex align-items-center gap-3">
                  <div className={`avatar-sm flex-shrink-0 bg-${item.color}-subtle text-${item.color} rounded-circle d-flex align-items-center justify-content-center fs-xl`}>
                    <i className={item.icon} />
                  </div>
                  <div>
                    <div className="text-muted mb-1">{item.label}</div>
                    <h4 className="mb-0">{isLoading ? "—" : item.value}</h4>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <Card className="border-0 shadow-sm">
          <Card.Header className="border-0 bg-transparent pt-4 px-4">
            <Row className="align-items-center g-3">
              <Col>
                <h5 className="mb-1">{isAdmin ? "All support tickets" : "Your support tickets"}</h5>
                <p className="text-muted mb-0">Track issues raised with the Tradesmens support team.</p>
              </Col>
              <Col xs={12} md="auto">
                <div className="d-flex flex-column flex-sm-row gap-2">
                  <div className="search-box">
                    <Form.Control
                      type="search"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Search tickets"
                      aria-label="Search support tickets"
                    />
                    <i className="ri-search-line search-icon" />
                  </div>
                  <Button type="button" variant="primary" onClick={openLoganSupportTicket}>
                    <i className="bi bi-plus-circle me-1" /> Create Ticket
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Header>

          <Card.Body className="px-4 pb-4">
            {error && (
              <Alert variant="danger" className="d-flex align-items-center justify-content-between gap-3">
                <span>{error}</span>
                <Button variant="outline-danger" size="sm" onClick={() => void loadTickets()}>Retry</Button>
              </Alert>
            )}

            {isLoading ? (
              <div className="text-center py-5" role="status">
                <Spinner animation="border" variant="primary" />
                <div className="text-muted mt-3">Loading support tickets…</div>
              </div>
            ) : !error && filteredTickets.length > 0 ? (
              <TableContainer
                isPagination
                columns={columns}
                data={filteredTickets}
                customPageSize={10}
                divClassName="table-responsive"
                tableClass="table table-centered align-middle table-nowrap mb-0"
                theadClass="table-light"
                PaginationClassName="align-items-center mt-4 pt-3"
                isBordered={false}
                SearchPlaceholder=""
              />
            ) : !error ? (
              <div className="text-center py-5">
                <div className="avatar-md mx-auto mb-3 bg-primary-subtle text-primary rounded-circle d-flex align-items-center justify-content-center fs-3">
                  <i className={search ? "ph-magnifying-glass" : "ph-ticket"} />
                </div>
                <h5>{search ? "No matching tickets" : "No support tickets yet"}</h5>
                <p className="text-muted mb-3">
                  {search ? "Try a different ticket ID, subject, status, or date." : "Need help? Create a ticket and the support team will review it."}
                </p>
                {!search && <Button onClick={openLoganSupportTicket}>Create your first ticket</Button>}
              </div>
            ) : null}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default ListViewTickets;
